import { z } from "zod";

/**
 * Booking Engine (Phase 2a) — mirrors app/schemas/booking.py +
 * app/services/booking_availability.py's response shapes.
 */

// "studio" added in the Membership + Hour-Bank Engine (Phase 2b) for "The
// Wilder Studio" resource (app/models/resource.py) — booked via
// api/hooks/useMemberships.ts, never through the room-booking flow below.
export const ResourceKindSchema = z.enum(["event_room", "wellness_room", "studio"]);
export type ResourceKind = z.infer<typeof ResourceKindSchema>;

export const BookingStatusSchema = z.enum(["inquiry", "confirmed", "cancelled", "completed"]);
export type BookingStatus = z.infer<typeof BookingStatusSchema>;

export const BOOKING_STATUS_LABELS: Record<BookingStatus, string> = {
  inquiry: "Inquiry",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  completed: "Completed",
};

export const BOOKING_STATUS_BADGE_VARIANT: Record<
  BookingStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  inquiry: "warning",
  confirmed: "info",
  cancelled: "danger",
  completed: "success",
};

export const ResourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: ResourceKindSchema,
  capacity: z.number().nullable(),
  weekday_price_cents: z.number(),
  weekend_price_cents: z.number(),
  is_whole_house: z.boolean(),
  active: z.boolean(),
});
export type Resource = z.infer<typeof ResourceSchema>;

export const ResourceListResponseSchema = z.object({ items: z.array(ResourceSchema) });
export type ResourceListResponse = z.infer<typeof ResourceListResponseSchema>;

export const BookingSlotSchema = z.object({
  id: z.string(),
  kind: ResourceKindSchema,
  code: z.string(),
  label: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  sort_order: z.number(),
});
export type BookingSlot = z.infer<typeof BookingSlotSchema>;

export const BookingSlotListResponseSchema = z.object({ items: z.array(BookingSlotSchema) });
export type BookingSlotListResponse = z.infer<typeof BookingSlotListResponseSchema>;

export const SlotAvailabilitySchema = z.object({
  slot_id: z.string().nullable(),
  code: z.string(),
  label: z.string(),
  start_time: z.string().nullable(),
  end_time: z.string().nullable(),
  available: z.boolean(),
  price_cents: z.number(),
  extra_hour_price_cents: z.number().nullable(),
});
export type SlotAvailability = z.infer<typeof SlotAvailabilitySchema>;

export const DayAvailabilitySchema = z.object({
  date: z.string(),
  slots: z.array(SlotAvailabilitySchema),
});
export type DayAvailability = z.infer<typeof DayAvailabilitySchema>;

export const ResourceAvailabilitySchema = z.object({
  resource_id: z.string(),
  resource_name: z.string(),
  resource_kind: ResourceKindSchema,
  days: z.array(DayAvailabilitySchema),
});
export type ResourceAvailability = z.infer<typeof ResourceAvailabilitySchema>;

export const BookingSchema = z.object({
  id: z.string(),
  resource_id: z.string(),
  resource_name: z.string(),
  resource_kind: ResourceKindSchema,
  slot_id: z.string().nullable(),
  slot_label: z.string().nullable(),
  booking_date: z.string(),
  extra_hour: z.boolean(),
  status: BookingStatusSchema,
  contact_id: z.string().nullable(),
  headcount: z.number().nullable(),
  occasion: z.string().nullable(),
  notes: z.string().nullable(),
  price_cents: z.number(),
  invoice_id: z.string().nullable(),
  is_shadow: z.boolean(),
  group_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
});
export type Booking = z.infer<typeof BookingSchema>;

export const BookingListResponseSchema = z.object({
  items: z.array(BookingSchema),
  next_cursor: z.string().nullable(),
});
export type BookingListResponse = z.infer<typeof BookingListResponseSchema>;

// ---- Mutation inputs ------------------------------------------------------

export interface CreateBookingInput {
  resource_id: string;
  slot_id?: string | null;
  booking_date: string;
  extra_hour?: boolean;
  status?: BookingStatus;
  contact_id?: string | null;
  headcount?: number | null;
  occasion?: string | null;
  notes?: string | null;
}

export interface PatchBookingInput {
  id: string;
  contact_id?: string | null;
  headcount?: number | null;
  occasion?: string | null;
  notes?: string | null;
}
